import React, { useEffect, useState, useMemo } from 'react';
import {
    Box, Paper, Typography, Chip, IconButton, Tooltip, TextField, MenuItem,
    Stack, Table, TableHead, TableRow, TableCell, TableBody, TablePagination,
    CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button,
    LinearProgress, ThemeProvider, InputAdornment, Select, FormControl, InputLabel,
    Divider,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../services/api';
import { useNotify } from '../../components/Datatable/NotifyProvider';
import lightTheme from '../../themes/lightTheme';

const STATUS = {
    queued:  { label: 'Na fila',     color: 'default',  icon: HourglassBottomIcon },
    running: { label: 'Executando',  color: 'info',     icon: PlaylistPlayIcon },
    done:    { label: 'OK',          color: 'success',  icon: CheckCircleIcon },
    failed:  { label: 'Falhou',      color: 'error',    icon: ErrorIcon },
};

const TYPE_LABEL = {
    dns_sync_blacklist:        'Sync blacklist',
    dns_sync_acl:              'Sync ACL',
    dns_sync_localzone:        'Sync zonas locais',
    dns_sync_node:             'Sync nó DNS',
    dns_validate_credentials:  'Valida SSH',
};

function fmtDate(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('pt-BR');
}

function fmtDuration(start, end) {
    if (!start) return '—';
    const a = new Date(start).getTime();
    const b = end ? new Date(end).getTime() : Date.now();
    const sec = Math.max(0, (b - a) / 1000);
    if (sec < 60) return `${sec.toFixed(1)}s`;
    if (sec < 3600) return `${(sec / 60).toFixed(1)}min`;
    return `${(sec / 3600).toFixed(1)}h`;
}

export default function TasksPage() {
    const notify = useNotify();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [perPage, setPerPage] = useState(20);
    const [filters, setFilters] = useState({ query: '', status: '', type: '' });
    const [detailTask, setDetailTask] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const body = {
                limit: perPage,
                offset: page * perPage,
                order_by: 'created_at',
                order: 'desc',
                query: filters.query || null,
                status: filters.status || null,
                type: filters.type || null,
            };
            const res = await api.post('/tasks/', body);
            setRows(res.data?.data || []);
            setTotal(res.data?.total || 0);
        } catch (e) {
            notify.setNotify({ open: true, message: e.response?.data?.detail || 'Erro ao listar tasks', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        document.title = 'IPV7 Tools - Eventos / Tasks';
        fetchData();
        // eslint-disable-next-line
    }, [page, perPage]);

    // Auto-refresh: se há task running/queued, refresh a cada 5s.
    useEffect(() => {
        const hasActive = rows.some(r => r.status === 'queued' || r.status === 'running');
        if (!hasActive) return;
        const id = setInterval(fetchData, 5000);
        return () => clearInterval(id);
        // eslint-disable-next-line
    }, [rows]);

    // Filtro client-side de query funciona junto com server-side (query no backend
    // só filtra username/type)
    const reload = () => { setPage(0); fetchData(); };

    const onFilterChange = (key) => (e) => {
        setFilters(f => ({ ...f, [key]: e.target.value }));
    };

    return (
        <ThemeProvider theme={lightTheme}>
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: 'calc(100vh - 100px)', p: { xs: 1, md: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="h5" sx={{ fontFamily: 'Montserrat', fontWeight: 300 }}>
                    Eventos / Fila de Tasks
                </Typography>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Atualizar">
                        <IconButton onClick={fetchData}>
                            <RefreshIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </Box>

            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                Histórico completo das tasks DNS executadas em background. Tasks ativas
                atualizam automaticamente.
            </Typography>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    <TextField
                        size="small"
                        placeholder="Buscar (user, tipo)…"
                        value={filters.query}
                        onChange={onFilterChange('query')}
                        onKeyDown={(e) => { if (e.key === 'Enter') reload(); }}
                        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                        sx={{ minWidth: 220 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={filters.status} onChange={onFilterChange('status')}>
                            <MenuItem value="">Todos</MenuItem>
                            <MenuItem value="queued">Na fila</MenuItem>
                            <MenuItem value="running">Executando</MenuItem>
                            <MenuItem value="done">Concluídas</MenuItem>
                            <MenuItem value="failed">Falhou</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Tipo</InputLabel>
                        <Select label="Tipo" value={filters.type} onChange={onFilterChange('type')}>
                            <MenuItem value="">Todos</MenuItem>
                            {Object.entries(TYPE_LABEL).map(([k, v]) => (
                                <MenuItem key={k} value={k}>{v}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button onClick={reload} variant="outlined">Filtrar</Button>
                </Stack>
            </Paper>

            <Paper>
                {loading && <LinearProgress />}
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ width: 60 }}>#</TableCell>
                            <TableCell sx={{ width: 180 }}>Tipo</TableCell>
                            <TableCell sx={{ width: 120 }}>Status</TableCell>
                            <TableCell sx={{ width: 130 }}>Usuário</TableCell>
                            <TableCell sx={{ width: 170 }}>Criada em</TableCell>
                            <TableCell sx={{ width: 100 }}>Duração</TableCell>
                            <TableCell>Resumo / erro</TableCell>
                            <TableCell align="right" sx={{ width: 60 }}>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                                    Nenhuma task encontrada.
                                </TableCell>
                            </TableRow>
                        )}
                        {rows.map((t) => {
                            const meta = STATUS[t.status] || STATUS.queued;
                            const Icon = meta.icon;
                            return (
                                <TableRow key={t.id} hover sx={{ cursor: 'pointer' }} onClick={() => setDetailTask(t)}>
                                    <TableCell>{t.id}</TableCell>
                                    <TableCell>{TYPE_LABEL[t.type] || t.type}</TableCell>
                                    <TableCell>
                                        <Chip
                                            size="small" icon={<Icon fontSize="small" />}
                                            label={meta.label} color={meta.color} variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{t.username || '—'}</TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>{fmtDate(t.created_at)}</TableCell>
                                    <TableCell sx={{ fontSize: 12 }}>{fmtDuration(t.created_at, t.updated_at)}</TableCell>
                                    <TableCell sx={{ fontSize: 12, fontFamily: 'monospace', maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {t.error ? (
                                            <Typography variant="caption" sx={{ color: 'error.main' }}>{t.error}</Typography>
                                        ) : (
                                            renderSummary(t)
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small">
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={perPage}
                    onRowsPerPageChange={(e) => { setPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Por página:"
                />
            </Paper>

            <TaskDetailDialog
                task={detailTask}
                onClose={() => setDetailTask(null)}
            />
        </Box>
        </ThemeProvider>
    );
}

function renderSummary(t) {
    const p = t.progress || {};
    if (p.per_node) {
        const nodes = Object.values(p.per_node);
        const ok = nodes.filter((n) => n.status === 'ok').length;
        return `${ok}/${nodes.length} nós OK${p.domain_count ? ` · ${p.domain_count} domínios` : ''}${p.range_count ? ` · ${p.range_count} ranges` : ''}${p.zone_count ? ` · ${p.zone_count} zonas` : ''}`;
    }
    if (p.phase) return `Fase: ${p.phase}`;
    return '—';
}

function TaskDetailDialog({ task, onClose }) {
    if (!task) return null;
    const meta = STATUS[task.status] || STATUS.queued;
    const Icon = meta.icon;

    return (
        <Dialog open={!!task} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="h6">Task #{task.id}</Typography>
                    <Chip
                        size="small" icon={<Icon fontSize="small" />}
                        label={meta.label} color={meta.color} variant="outlined"
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {TYPE_LABEL[task.type] || task.type}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2}>
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>Usuário</Typography>
                        <Typography variant="body2">{task.username || '—'}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 4 }}>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Criada em</Typography>
                            <Typography variant="body2">{fmtDate(task.created_at)}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Atualizada em</Typography>
                            <Typography variant="body2">{fmtDate(task.updated_at)}</Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Duração</Typography>
                            <Typography variant="body2">{fmtDuration(task.created_at, task.updated_at)}</Typography>
                        </Box>
                    </Box>

                    {task.arq_job_id && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Arq job ID</Typography>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{task.arq_job_id}</Typography>
                        </Box>
                    )}

                    {task.error && (
                        <Box>
                            <Typography variant="caption" sx={{ color: 'error.main', display: 'block' }}>Erro</Typography>
                            <Box sx={{ p: 1.5, bgcolor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 1, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                {task.error}
                            </Box>
                        </Box>
                    )}

                    <Divider />

                    <JsonField title="Payload" value={task.payload} />
                    <JsonField title="Progress" value={task.progress} />
                    <JsonField title="Result" value={task.result} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}

function JsonField({ title, value }) {
    if (!value) {
        return (
            <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>{title}</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>—</Typography>
            </Box>
        );
    }
    return (
        <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{title}</Typography>
            <Box sx={{
                p: 1.5,
                bgcolor: 'grey.100',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                fontFamily: 'monospace',
                fontSize: 11,
                maxHeight: 240,
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
            }}>
                {JSON.stringify(value, null, 2)}
            </Box>
        </Box>
    );
}
